import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api/client';
import type { UserDto } from '../../lib/api/endpoints';
import type { UserRole } from '../../types/user';
import type { AdminUser } from '../../hooks/useUsers';
import type { Vehicle } from '../../types/vehicle';

type AdminTab = 'users' | 'vehicles' | 'devices' | 'subunits';

interface AdminOption {
  id: string;
  name: string;
}

/**
 * La fila que se esta editando. Su forma la decide `tab`, no el tipo:
 * `AdminUser` en 'users', `Vehicle` en 'vehicles', y algo con `id`+`name` en
 * 'subunits'. TypeScript no puede estrechar por `tab`, asi que cada rama de
 * `useEffect` y del JSX lo hace explicitamente con un `as` al tipo de SU
 * pestana — un cast localizado y comprobable, no un `any` que apaga el
 * compilador en todo el archivo.
 */
export type AdminEditableItem = AdminUser | Vehicle | AdminOption;

/**
 * Estado del formulario. Es polimorfico por `tab`: cada pestana llena su
 * subconjunto y `set(key, value)` escribe por nombre de campo. `role` y
 * `status` viven como `string` a proposito — son el valor de un `<select>`, y
 * se estrechan al enum del contrato al armar el payload, que es la frontera
 * real con el backend.
 */
type AdminFormData = {
  displayName?: string;
  email?: string;
  role?: string;
  status?: string;
  subunitId?: string;
  driverId?: string;
  documentId?: string;
  phone?: string;
  plate?: string;
  brand?: string;
  model?: string;
  year?: number | string;
  name?: string;
};

/**
 * Lo que el modal entrega al guardar. Su forma tambien la decide `tab`:
 * `UserCreate`/`UserUpdate` en 'users', los campos de vehiculo en 'vehicles',
 * `{ name }` en 'subunits'. `AdminPage` lo estrecha al DTO que toca segun la
 * pestana activa.
 */
export type AdminFormPayload = Record<string, unknown>;

/** Resultado del correo de invitación que devuelve `POST /users` (201). */
export interface ResultadoInvitacion {
  status: 'SENT' | 'FAILED';
  email: string;
  detail?: string | null;
}

interface AdminFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  tab: AdminTab;
  initialData?: AdminEditableItem | null;
  // Para 'users': la promesa resuelve { invitation } en el alta (el
  // core la genera y solo viaja una vez, openapi.yaml `UserCreated`) o vacío
  // en la edición. `warning` es para un guardado que sí llamó al backend
  // pero cuyo resultado no coincide del todo con lo pedido (p.ej. el core
  // no puede vaciar `subunitIds` por PATCH — EP-17-crud-gestion.md §7): se
  // muestra en vez de cerrar en silencio, para no fingir un éxito completo.
  // Un rechazo mantiene el modal abierto y muestra el error.
  onSave: (itemData: AdminFormPayload) => void | Promise<{ invitation?: ResultadoInvitacion; warning?: string } | void>;
  // Datos reales (GET /subunits, GET /drivers) para los formularios de
  // usuarios y vehículos — nunca de las listas hardcodeadas de abajo.
  // GET /subunits ya llega acotado por el backend a lo que el actor alcanza
  // (Scope().Visibility(), listados.go) — un Supervisor recibe solo la suya.
  subunitOptions?: AdminOption[];
  // Para 'vehicles': todos los conductores del tenant (cualquiera se puede
  // asignar a un vehículo, a diferencia del alta de un Operador).
  allDriverOptions?: AdminOption[];
  // Rol de quien tiene el modal abierto — un Supervisor solo administra
  // Operadores de su sub-unidad (PRODUCT.md §4.8), nunca otro rol.
  currentUserRole?: UserRole;
}

const USER_ROLE_OPTIONS: { value: UserDto['role']; label: string }[] = [
  { value: 'ADMINISTRADOR', label: 'Administrador' },
  { value: 'SUPERVISOR', label: 'Supervisor' },
  { value: 'OPERADOR', label: 'Operador' },
  { value: 'EJECUTIVO', label: 'Ejecutivo' },
];

function describeSaveError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.detail ? `${err.title}: ${err.detail}` : err.title;
  }
  if (err instanceof Error) return err.message;
  return 'Ocurrió un error inesperado al guardar.';
}

// Opciones {value, label} — id de sub-unidad,
// enum de rol del contrato, etc., donde el texto mostrado no es el valor real.
const OptionSelectField = ({
  id, label, value, options, onChange, required, disabled,
}: {
  id: string; label: string; value: string;
  options: { value: string; label: string }[]; onChange: (v: string) => void;
  required?: boolean; disabled?: boolean;
}) => (
  <div className="input-wrapper">
    <label className="input-label" htmlFor={id}>{label}{required && ' *'}</label>
    <select
      id={id}
      required={required}
      disabled={disabled}
      className="input-field"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', padding: '0.625rem 0.875rem',
        background: 'var(--bg-raised)', color: 'var(--text-primary)',
        border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
        fontSize: '14px', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

export function AdminFormModal({
  isOpen, onClose, tab, initialData, onSave,
  subunitOptions = [], allDriverOptions = [], currentUserRole,
}: AdminFormModalProps) {
  const isSupervisorActor = currentUserRole === 'supervisor';
  const [formData, setFormData] = useState<AdminFormData>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [invitacion, setInvitacion] = useState<ResultadoInvitacion | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  useEffect(() => {
    setSaveError('');
    setSaving(false);
    setInvitacion(null);
    setSaveWarning(null);
    if (initialData) {
      if (tab === 'users') {
        const user = initialData as AdminUser;
        setFormData({
          displayName: user.displayName ?? '',
          email: user.email,
          role: user.roleValue,
          subunitId: user.subunitIds?.[0] ?? '',
          driverId: '',
          status: user.status,
        });
      } else if (tab === 'vehicles') {
        const vehicle = initialData as Vehicle;
        setFormData({
          plate: vehicle.plate,
          brand: vehicle.brand ?? '',
          model: vehicle.model ?? '',
          // Sin `|| new Date().getFullYear()` a propósito: si el vehículo no
          // tiene año registrado, un valor por defecto se colaría al PATCH
          // en cuanto se guardara cualquier otro campo, sobrescribiéndolo
          // sin que el admin lo haya tocado (encontrado verificando en vivo,
          // 2026-09-01).
          year: vehicle.year || '',
          subunitId: vehicle.subUnitId || '',
          driverId: vehicle.driverId || '',
        });
      } else if (tab === 'subunits') {
        setFormData({ name: (initialData as AdminOption).name ?? '' });
      } else {
        setFormData({ ...initialData } as AdminFormData);
      }
    } else {
      if (tab === 'users') {
        // Un Supervisor solo puede crear Operadores (PRODUCT.md §4.8) —
        // arrancar el selector en 'SUPERVISOR' le mostraría una opción que
        // no puede guardar.
        setFormData({ displayName: '', email: '', role: isSupervisorActor ? 'OPERADOR' : 'SUPERVISOR', subunitId: '', driverId: '', documentId: '', phone: '', status: 'ACTIVE' });
      } else if (tab === 'vehicles') {
        setFormData({ plate: '', brand: '', model: '', year: new Date().getFullYear(), subunitId: '', driverId: '' });
      } else if (tab === 'subunits') {
        setFormData({ name: '' });
      }
    }
  }, [initialData, tab, isOpen, isSupervisorActor]);


  const buildUserPayload = () => {
    // El campo de sub-unidad solo se muestra para SUPERVISOR y OPERADOR — si
    // el rol cambia a otro, `formData.subunitId` puede seguir teniendo el
    // valor viejo de cuando sí se mostraba (el campo se oculta, no se
    // limpia). Sin este guard, editar un Supervisor a Administrador seguía
    // mandando su sub-unidad anterior al backend aunque la UI ya no la
    // mostrara. OPERADOR se suma aquí por el issue #77: mandar `[]` para un
    // Operador hace que el backend lo rechace (403 si lo crea un Supervisor)
    // o lo deje huérfano y sin sub-unidad (si lo crea un Admin).
    const subunitIds = (formData.role === 'SUPERVISOR' || formData.role === 'OPERADOR') && formData.subunitId
      ? [formData.subunitId] : [];
    if (initialData) {
      return {
        displayName: formData.displayName || undefined,
        role: formData.role as UserDto['role'],
        subunitIds,
        status: formData.status as UserDto['status'],
      };
    }
    // Alta de OPERADOR = alta de conductor + cuenta (issue #196, decisión
    // 2026-10-14: el Operador ES el conductor). El modal no tiene el
    // `driverId` todavía; manda los datos del conductor en `newDriver` y
    // `AdminPage` crea primero el conductor (POST /drivers) y luego la cuenta
    // con el id resultante. Un conductor sin cuenta sigue siendo posible por
    // API, pero no se presenta como entidad aparte en este alta.
    if (formData.role === 'OPERADOR') {
      return {
        email: formData.email,
        displayName: formData.displayName || undefined,
        role: 'OPERADOR' as UserDto['role'],
        subunitIds,
        newDriver: {
          displayName: (formData.displayName || '').trim(),
          documentId: formData.documentId?.trim() || undefined,
          phone: formData.phone?.trim() || undefined,
          subunitId: formData.subunitId || undefined,
        },
      };
    }
    return {
      email: formData.email,
      displayName: formData.displayName || undefined,
      role: formData.role as UserDto['role'],
      subunitIds,
    };
  };

  // `driverId` no es parte de `VehicleCreate`/`VehicleUpdate` (la asignación
  // conductor↔vehículo es un recurso aparte, con vigencia — openapi.yaml
  // `/driver-assignments`). Va en el mismo payload para que `AdminPage` lo
  // separe y decida si hay que cerrar/crear una asignación.
  const buildVehiclePayload = () => ({
    ...(initialData ? {} : { plate: formData.plate }),
    brand: formData.brand || undefined,
    model: formData.model || undefined,
    year: formData.year ? Number(formData.year) : undefined,
    subunitId: formData.subunitId || undefined,
    driverId: formData.driverId || undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError('');

    if (tab === 'users') {
      if (!formData.role) {
        setSaveError('Selecciona un rol.');
        return;
      }
      if (formData.role === 'SUPERVISOR' && !formData.subunitId) {
        setSaveError('Selecciona una sub-unidad para el Supervisor.');
        return;
      }
      // Alta de OPERADOR = alta de conductor (issue #196): el nombre es
      // obligatorio porque es el `displayName` del conductor (DriverCreate lo
      // exige), no un dato decorativo de la cuenta.
      if (!initialData && formData.role === 'OPERADOR' && !formData.displayName?.trim()) {
        setSaveError('Escribe el nombre completo del Operador.');
        return;
      }
      // Issue #77: sin sub-unidad, un Supervisor recibe 403 (el backend no
      // puede verificar que el Operador cae dentro de su alcance) y un Admin
      // deja un Operador huérfano e invisible para cualquier Supervisor. Al
      // crear se elige directamente (el conductor nace con esa sub-unidad).
      if (formData.role === 'OPERADOR' && !formData.subunitId) {
        setSaveError('Selecciona una sub-unidad para el Operador.');
        return;
      }
    }

    if (tab === 'vehicles' && !formData.subunitId) {
      setSaveError('Selecciona una sub-unidad para el vehículo.');
      return;
    }

    const payload = tab === 'users' ? buildUserPayload() : tab === 'vehicles' ? buildVehiclePayload() : formData;

    setSaving(true);
    try {
      const result = await onSave(payload);
      if (result && result.invitation) {
        setInvitacion(result.invitation);
      } else if (result && result.warning) {
        setSaveWarning(result.warning);
      } else {
        onClose();
      }
    } catch (err) {
      setSaveError(describeSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof AdminFormData>(key: K, value: AdminFormData[K]) =>
    setFormData(prev => ({ ...prev, [key]: value }));

  const getTitle = () => {
    const action = initialData ? 'Editar' : 'Agregar';
    const entity = { users: 'Usuario', vehicles: 'Vehículo', devices: 'Dispositivo', subunits: 'Sub-unidad' }[tab];
    return `${action} ${entity}`;
  };

  if (invitacion) {
    const enviada = invitacion.status === 'SENT';
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={getTitle()} size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Hasta el 2026-09-18 aquí se mostraba una contraseña temporal que
              salía UNA sola vez. Si nadie la copiaba, la cuenta quedaba
              completa e inalcanzable — le pasó a operador@paitron.demo
              durante nueve días (issue #75). Ahora la credencial la pone la
              persona desde su propio correo. */}
          {enviada ? (
            <p style={{ fontSize: '14px', color: 'var(--text-primary)', margin: 0 }}>
              Usuario creado. Le enviamos un correo a <strong>{invitacion.email}</strong> para que
              defina su contraseña. El enlace vence en unas horas; si no le llega, puedes reenviarlo
              desde la lista de usuarios.
            </p>
          ) : (
            <div style={{
              padding: '0.875rem 1rem', background: 'rgba(245,158,11,0.12)',
              border: '1px solid #F59E0B', borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.5,
            }}>
              {/* El usuario SÍ quedó creado: repetir el alta chocaría con el
                  409 de correo repetido. Lo que falta es reenviar. */}
              <strong>El usuario quedó creado, pero el correo no salió.</strong>
              <br />
              {invitacion.detail ?? 'No se pudo enviar la invitación.'}
              <br />
              Reenvíala desde la lista de usuarios: no vuelvas a crear la cuenta.
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
            <Button variant="primary" size="sm" onClick={onClose}>Entendido</Button>
          </div>
        </div>
      </Modal>
    );
  }

  if (saveWarning) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={getTitle()} size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{
            padding: '0.875rem 1rem', background: 'rgba(245,158,11,0.12)',
            border: '1px solid #F59E0B', borderRadius: 'var(--radius-md)',
            color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.5,
          }}>
            {saveWarning}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
            <Button variant="primary" size="sm" onClick={onClose}>Entendido</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getTitle()} size="md">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* USERS FORM */}
        {tab === 'users' && (
          <>
            <Input id="admin-form-user-name"
              label={formData.role === 'OPERADOR' ? 'Nombre completo' : 'Nombre completo (opcional)'}
              placeholder="Ej. Juan Pérez"
              value={formData.displayName || ''} onChange={e => set('displayName', e.target.value)}
              required={formData.role === 'OPERADOR'} />
            <Input id="admin-form-user-email" label="Correo electrónico" type="email" placeholder="usuario@empresa.com"
              value={formData.email || ''} onChange={e => set('email', e.target.value)}
              required disabled={!!initialData} />
            <OptionSelectField id="admin-form-user-role" label="Rol del usuario" value={formData.role || 'SUPERVISOR'}
              options={(() => {
                // Un Supervisor solo administra Operadores (PRODUCT.md §4.8) —
                // nunca ve ni Administrador, ni Supervisor, ni Ejecutivo aquí.
                if (isSupervisorActor) return USER_ROLE_OPTIONS.filter(o => o.value === 'OPERADOR');
                // El rol OPERADOR requiere vincular un conductor en el alta
                // (driverId), algo que no existe al editar — por eso no se
                // ofrece como destino de conversión para un usuario que ya
                // nació con otro rol.
                return initialData && (initialData as AdminUser).roleValue !== 'OPERADOR'
                  ? USER_ROLE_OPTIONS.filter(o => o.value !== 'OPERADOR')
                  : USER_ROLE_OPTIONS;
              })()}
              onChange={v => setFormData(prev => ({ ...prev, role: v, subunitId: '', driverId: '' }))}
              required disabled={isSupervisorActor} />

            {formData.role === 'SUPERVISOR' && (
              <OptionSelectField id="admin-form-user-subunit" label="Sub-unidad / Región" value={formData.subunitId || ''}
                options={[{ value: '', label: 'Selecciona una sub-unidad' }, ...subunitOptions.map(s => ({ value: s.id, label: s.name }))]}
                onChange={v => set('subunitId', v)} required />
            )}

            {!initialData && formData.role === 'OPERADOR' && (
              <>
                {/* Issue #196 (decisión 2026-10-14): el Operador ES el
                    conductor. El alta crea el conductor (POST /drivers) y la
                    cuenta (POST /users con ese driverId) en un paso; no se
                    vincula uno existente. La sub-unidad se elige aquí y queda
                    tanto en el conductor como en la cuenta (issue #77). */}
                <OptionSelectField id="admin-form-user-subunit" label="Sub-unidad / Región" value={formData.subunitId || ''}
                  options={[{ value: '', label: 'Selecciona una sub-unidad' }, ...subunitOptions.map(s => ({ value: s.id, label: s.name }))]}
                  onChange={v => set('subunitId', v)} required />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                  <Input id="admin-form-user-document" label="Documento (opcional)" placeholder="Ej. 1234567890"
                    value={formData.documentId || ''} onChange={e => set('documentId', e.target.value)} />
                  <Input id="admin-form-user-phone" label="Teléfono (opcional)" type="tel" placeholder="Ej. 3001234567"
                    value={formData.phone || ''} onChange={e => set('phone', e.target.value)} />
                </div>
              </>
            )}

            {initialData && (initialData as AdminUser).roleValue === 'OPERADOR' && formData.role === 'OPERADOR' && (
              <>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                  El conductor vinculado a este Operador no se puede cambiar desde aquí.
                </p>
                <OptionSelectField id="admin-form-user-subunit-operador" label="Sub-unidad / Región" value={formData.subunitId || ''}
                  options={[{ value: '', label: 'Selecciona una sub-unidad' }, ...subunitOptions.map(s => ({ value: s.id, label: s.name }))]}
                  onChange={v => set('subunitId', v)} required />
              </>
            )}

            {initialData && (
              <OptionSelectField id="admin-form-user-status" label="Estado" value={formData.status || 'ACTIVE'}
                options={[{ value: 'ACTIVE', label: 'Activo' }, { value: 'INACTIVE', label: 'Inactivo' }]}
                onChange={v => set('status', v)} required />
            )}
          </>
        )}

        {/* VEHICLES FORM */}
        {tab === 'vehicles' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
              <Input id="admin-form-veh-plate" label="Placa del vehículo" placeholder="Ej. ABC-123"
                value={formData.plate || ''} onChange={e => set('plate', e.target.value.toUpperCase())}
                required disabled={!!initialData} />
              <OptionSelectField id="admin-form-veh-driver" label="Conductor asignado" value={formData.driverId || ''}
                options={[{ value: '', label: 'Sin asignar' }, ...allDriverOptions.map(d => ({ value: d.id, label: d.name }))]}
                onChange={v => set('driverId', v)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
              <Input id="admin-form-veh-brand" label="Marca" placeholder="Ej. Freightliner"
                value={formData.brand || ''} onChange={e => set('brand', e.target.value)} />
              <Input id="admin-form-veh-model" label="Modelo" placeholder="Ej. Cascadia"
                value={formData.model || ''} onChange={e => set('model', e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
              <Input id="admin-form-veh-year" label="Año" type="number"
                value={formData.year || new Date().getFullYear()} onChange={e => set('year', Number(e.target.value))} />
              <OptionSelectField id="admin-form-veh-subunit" label="Sub-unidad / Región" value={formData.subunitId || ''}
                options={[{ value: '', label: 'Selecciona una sub-unidad' }, ...subunitOptions.map(s => ({ value: s.id, label: s.name }))]}
                onChange={v => set('subunitId', v)} required />
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
              La asignación de dispositivo GPS todavía no está conectada a la API (el contrato no
              tiene forma de editarla desde aquí) — se administra aparte, en la pestaña Dispositivos.
            </p>
          </>
        )}

        {/* SUBUNITS FORM */}
        {tab === 'subunits' && (
          <Input id="admin-form-sub-name" label="Nombre de la Sub-unidad" placeholder="Ej. Región Occidente"
            value={formData.name || ''} onChange={e => set('name', e.target.value)} required />
        )}

        {saveError && (
          <div style={{ padding: '0.625rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid #EF4444', borderRadius: 'var(--radius-md)', color: '#EF4444', fontSize: '12px', fontWeight: 600 }}>
            {saveError}
          </div>
        )}

        {/* Submit Actions */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem', borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
          <Button variant="ghost" size="sm" type="button" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button variant="primary" size="sm" type="submit" isLoading={saving}>
            {initialData ? 'Guardar Cambios' : 'Crear Registro'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
